import React from 'react';
import DialogDataComponent, {executeComponentCallback} from 'Core/components/DialogDataComponent';
import PropTypes from "prop-types";
import {connect} from 'react-redux';
import {getGlobalActions} from 'Core/helpers/redux';
import * as actions from 'Pages/apps/default/customerSegments/actions'
import FormWrapper, {FormField} from 'Core/components/advanced/FormWrapper';
import {FORM_FIELD_LABEL_POSITION} from 'Core/components/advanced/FormWrapper/FormField';
import CustomerSegmentSelectInput from 'Components/input/CustomerSegmentSelectInput';
import Label from 'Core/components/display/Label';
import {icon_font_info_circle_symbol} from 'Config/app';
import DataValueValidation from 'Core/validation';
import {hideLoading, showDialogLoading} from 'Core/helpers/loading';
import {getString} from 'Core/helpers/data';

/**
 * Get all actions used by this component
 * @type {Object}
 */
const allActions = getGlobalActions(actions);

class SelectCustomerSegmentDialog extends DialogDataComponent {
	constructor(props) {
		super(props, {
			data: {
				/** @type {string} - DB ID of the selected customer segment */
				segmentId: '',
			},
		}, {
			translationPath: 'SelectCustomerSegmentDialog',
			domPrefix: 'select-customer-segment-dialog-component',
			disableLoad: true,
		});
	}

	/**
	 * Default component's data validation method
	 *
	 * @return {boolean} True if component's data validation passed successfully.
	 */
	validate() {
		const dataValidation = new DataValueValidation();
		const dataToValidate = this.getData();

		dataValidation.addRule('segmentId', 'required');
		
		// Run validation
		const validationErrors = dataValidation.run(dataToValidate);
		if (validationErrors) this.setValidationErrors('', validationErrors).then();
		else this.clearValidationErrors().then();
		return !validationErrors;
	}

	/** @inheritDoc */
	save() {
		const {dialogGUIID, dialogOptions, fetchCustomerSegmentsItemAction} = this.props;

		// Do the validation
		const isValid = this.validate();

		// If validation is successful
		if(isValid){
			const loading = showDialogLoading(getString(dialogOptions, 'id'));
			// Load full customer segment data based on the selected customer segment
			this.executeAbortableAction(fetchCustomerSegmentsItemAction, this.getValue('segmentId'))
				// Trigger component's onSave event with segment data as param
				.then(segment => executeComponentCallback(this.props.onSave, segment, dialogGUIID))
				.finally(() => hideLoading(loading))
		}
	}

	render() {
		const {projectId, description, notice, saveBtnIcon, saveBtnLabel} = this.props;
		const title = (!!this.getProp('title') ? this.getProp('title') : this.t('title'))
		
		return this.renderDialog(
			this.renderTitle(title),
			(
				<FormWrapper className={this.getOption('domPrefix')}>
					<br />
					{!!description ? <><Label element="div" content={description} /><br /></> : null}
					<FormField
						required={true}
						label={this.t('segmentIdField')}
						labelPosition={FORM_FIELD_LABEL_POSITION.STACKED}
						errorMessages={this.getValidationErrors('segmentId')}
					>
						<CustomerSegmentSelectInput
							projectId={projectId}
							isClearable={true}
							value={this.getValue('segmentId')}
							onChange={v => this.setValue('segmentId', v)}
						/>
					</FormField>
					{!!notice ? 
						<Label 
							element="small" 
							elementProps={{className: 'notice warning'}} 
							icon={icon_font_info_circle_symbol}
							content={notice} 
						/> 
						: null
					}
				</FormWrapper>
			),
			{saveIcon: saveBtnIcon, saveLabel: saveBtnLabel}
		);
	}
}

/**
 * Define component's own props that can be passed to it by parent components
 */
SelectCustomerSegmentDialog.propTypes = {
	// Unique GUI ID of the dialog
	// @note This is automatically sent by the global Dialog component.
	dialogGUIID: PropTypes.string,
	// Dialog options
	// @note This is automatically sent by the global Dialog component.
	dialogOptions: PropTypes.object,
	// Action used to close the dialog
	// @note This is automatically sent by the global Dialog component.
	dialogCloseAction: PropTypes.func,
	
	// DB ID of the project
	// @type {string}
	projectId: PropTypes.string,
	// Dialog title
	title: PropTypes.string,
	// Dialog description
	description: PropTypes.string,
	// Dialog notice
	notice: PropTypes.string,
	// Save button icon to override the default one
	saveBtnIcon: PropTypes.string,
	// Save button label to override the default one
	saveBtnLabel: PropTypes.string,
	
	// Events
	onClose: PropTypes.func,
};

export default connect(null, allActions, null, {forwardRef: true})(SelectCustomerSegmentDialog);